import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Users, MapPin, ArrowLeft } from "lucide-react";
import { getRegion, getRegionWeather } from "@/lib/api";
import { T } from "@/context/LanguageContext";
import { Seo } from "@/components/site/Seo";
import { itemListJsonLd } from "@/lib/seo";

const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";
const slugify = (n) => n.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");
const pascal = (s) => (s || "cloud").split("-").map((w) => w[0].toUpperCase() + w.slice(1)).join("");
const WIcon = ({ name, className }) => {
  const C = Icons[pascal(name)] || Icons.Cloud;
  return <C className={className} />;
};

export default function RegionHub() {
  const { key } = useParams();
  const [data, setData] = useState(null);
  const [weather, setWeather] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [hover, setHover] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setData(null); setNotFound(false); setWeather(null);
    getRegion(key).then(setData).catch(() => setNotFound(true));
    getRegionWeather(key).then(setWeather).catch(() => {});
  }, [key]);

  const memberSet = useMemo(() => new Set(data?.map_slugs || []), [data]);

  if (notFound) return <div className="mx-auto max-w-3xl px-4 py-24 text-center"><h1 className="font-display text-2xl font-black text-navy">Region not found</h1><Link to="/explore/states" className="mt-4 inline-block text-oldglory-red">← Explore states</Link></div>;
  if (!data) return <div className="mx-auto max-w-3xl px-4 py-24 text-center"><p className="animate-pulse font-display text-xl text-navy">Loading region…</p></div>;

  return (
    <div className="bg-parchment">
      <Seo
        title={`${data.label} — USAPulse Region Hub`}
        path={`/region/${data.key}`}
        description={data.description}
        image={data.og_image}
        jsonLd={[
          itemListJsonLd(data.label, data.states.map((s) => ({ name: s.official_name, url_path: s.url_path }))),
          ...(data.faq?.length ? [{
            "@context": "https://schema.org", "@type": "FAQPage",
            mainEntity: data.faq.map((f) => ({
              "@type": "Question", name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }] : []),
        ]}
      />
      <div className="mx-auto max-w-[1500px] px-4 py-12">
        <Link to="/explore/states" className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-oldglory-red" data-testid="region-back">
          <ArrowLeft className="h-4 w-4" /> <T>All regions</T>
        </Link>

        <div className="grid gap-8 lg:grid-cols-[1fr_1.1fr] lg:items-center">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-oldglory-red"><T>Historical Region</T></div>
            <h1 className="mt-1 font-display text-4xl font-black tracking-tight text-navy sm:text-5xl" data-testid="region-title">{data.label}</h1>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-ink/85"><T>{data.description}</T></p>
            <div className="mt-5 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-1.5 text-sm font-semibold text-white"><MapPin className="h-4 w-4" /> {data.state_count} <T>states</T></span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-1.5 text-sm font-semibold text-navy ring-1 ring-navy/15"><Users className="h-4 w-4 text-oldglory-red" /> {data.total_population.toLocaleString()} <T>residents</T></span>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-2xl border border-navy/15 bg-white shadow-xl" data-testid="region-map">
            <ComposableMap projection="geoAlbersUsa" width={980} height={560} style={{ width: "100%", height: "auto" }}>
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const slug = slugify(geo.properties.name);
                    const member = memberSet.has(slug);
                    const isHover = hover === slug && member;
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        onMouseEnter={() => member && setHover(slug)}
                        onMouseLeave={() => setHover(null)}
                        onClick={() => member && navigate(`/place/us/${slug}`)}
                        data-testid={`region-map-${slug}`}
                        style={{
                          default: { fill: member ? (isHover ? "#c8102e" : "#0a2a5e") : "#e8dcc4", stroke: "#fff", strokeWidth: 0.6, outline: "none", transition: "fill 0.2s" },
                          hover: { fill: member ? "#c8102e" : "#e8dcc4", stroke: "#fff", strokeWidth: 0.8, outline: "none", cursor: member ? "pointer" : "default" },
                          pressed: { fill: "#a10d25", outline: "none" },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
            </ComposableMap>
          </div>
        </div>

        {weather?.states?.length > 0 && (
          <section className="mt-10" data-testid="region-weather-strip">
            <div className="mb-3 flex items-center gap-2">
              <Icons.CloudSun className="h-4 w-4 text-oldglory-red" />
              <h2 className="font-display text-sm font-black uppercase tracking-widest text-navy"><T>Weather across the region right now</T></h2>
              <span className="text-[10px] text-muted-foreground">· Open-Meteo</span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {weather.states.map((s) => (
                <Link key={s.geo_id} to={`/place${s.url_path}`} className="flex min-w-[130px] shrink-0 items-center gap-3 rounded-xl border border-border bg-white px-4 py-3 transition-all hover:border-navy/30 hover:shadow-md" data-testid={`region-weather-${s.geo_id}`}>
                  <WIcon name={s.icon} className="h-7 w-7 shrink-0 text-oldglory-red" />
                  <div className="min-w-0">
                    <div className="truncate text-xs font-semibold text-navy">{s.name}</div>
                    <div className="font-display text-xl font-black leading-none text-navy">{s.temp_f}°<span className="ml-1 text-[10px] font-normal text-muted-foreground">{s.condition}</span></div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-12 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4" data-testid="region-states">
          {data.states.map((s, i) => (
            <motion.div key={s.geo_id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 8) * 0.04 }}>
              <Link to={`/place${s.url_path}`} className="group block overflow-hidden rounded-xl bg-white shadow-md ring-1 ring-border transition-all hover:shadow-2xl hover:ring-navy/30" data-testid={`region-card-${s.geo_id}`}>
                <div className="relative h-40 overflow-hidden">
                  <img src={s.image_url} alt={s.official_name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <div className="font-display text-lg font-bold text-white">{s.official_name}</div>
                    <div className="text-xs text-white/80">{s.nickname}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5 text-xs text-muted-foreground">
                  <span>{s.capital}</span>
                  <span>{s.population?.toLocaleString()}</span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {data.long_intro && (
          <section className="mt-14 max-w-3xl" data-testid="region-about">
            <h2 className="mb-3 font-display text-2xl font-black text-navy">About {data.label}</h2>
            <p className="text-base leading-relaxed text-ink/85"><T>{data.long_intro}</T></p>
          </section>
        )}

        {data.faq?.length > 0 && (
          <section className="mt-12 max-w-3xl" data-testid="region-faq">
            <h2 className="mb-4 font-display text-2xl font-black text-navy"><T>Frequently asked questions</T></h2>
            <div className="space-y-3">
              {data.faq.map((f, i) => (
                <details key={i} className="group rounded-xl border border-border bg-white p-4" data-testid={`region-faq-${i}`}>
                  <summary className="flex cursor-pointer items-center justify-between font-semibold text-navy">
                    <span>{f.q}</span>
                    <span className="text-oldglory-red transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-2 text-sm leading-relaxed text-ink/80">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
